import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Heading as HeadingIcon,
  AlignLeft,
  List,
  MessageSquare,
  AlertTriangle,
  Table as TableIcon,
  Image as ImageIcon,
  BarChart3,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  X,
  Loader2,
  Upload,
} from 'lucide-react';
import {
  uploadPortalPhoto,
  deletePortalPhoto,
  isStorageUrl,
} from '@/lib/portalStorage';
import { useResolvedImageUrl } from '@/components/estimates/ProposalImage';
import { toast } from 'sonner';

/* ---------------------------------------------------------------------------
 * SectionBlocksEditor — the "open-doc" content editor for a Scope section.
 *
 * Renders a section's rich content blocks (heading / paragraph / bullets /
 * callout / caveat) with an add-block toolbar and per-block reorder + delete.
 * Bullet items are edited as one-per-line text for speed. This is what turns
 * the Scope layout into a document editor instead of a single notes box.
 *
 * Props:
 *   blocks    — section.blocks array
 *   onAdd     (type)                → add a block of `type`
 *   onUpdate  (blockId, patch)      → patch a block
 *   onRemove  (blockId)             → delete a block
 *   onMove    (blockId, dir)        → reorder (-1 up, +1 down)
 * ------------------------------------------------------------------------- */

const ADD_TYPES = [
  { type: 'heading', label: 'Heading', Icon: HeadingIcon },
  { type: 'paragraph', label: 'Text', Icon: AlignLeft },
  { type: 'bullets', label: 'Bullets', Icon: List },
  { type: 'callout', label: 'Callout', Icon: MessageSquare },
  { type: 'caveat', label: 'Caveat', Icon: AlertTriangle },
  { type: 'table', label: 'Table', Icon: TableIcon },
  { type: 'image', label: 'Photo', Icon: ImageIcon },
  { type: 'chart', label: 'Chart', Icon: BarChart3 },
];

const TONE_OPTS = [
  { value: 'included', label: 'Included' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
];

const fieldCls =
  'bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60';

const Pill = ({ active, children, ...props }) => (
  <button
    type="button"
    className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.12em] rounded-md border transition-colors ${
      active
        ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
        : 'border-border bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground'
    }`}
    {...props}
  >
    {children}
  </button>
);

const TableBlockEditor = ({ block, set }) => {
  const columns = Array.isArray(block.columns) ? block.columns : [];
  const rows = Array.isArray(block.rows) ? block.rows : [];

  const setColumn = (i, val) => {
    const next = columns.slice();
    next[i] = val;
    set({ columns: next });
  };
  const addColumn = () =>
    set({
      columns: [...columns, ''],
      rows: rows.map((r) => [...r, '']),
    });
  const removeColumn = (i) => {
    if (columns.length <= 1) return;
    set({
      columns: columns.filter((_, ci) => ci !== i),
      rows: rows.map((r) => r.filter((_, ci) => ci !== i)),
    });
  };
  const setCell = (ri, ci, val) => {
    const next = rows.map((r) => r.slice());
    next[ri][ci] = val;
    set({ rows: next });
  };
  const addRow = () => set({ rows: [...rows, columns.map(() => '')] });
  const removeRow = (ri) => set({ rows: rows.filter((_, i) => i !== ri) });

  return (
    <div className="space-y-2">
      {/* Header cells */}
      <div className="flex items-center gap-1.5">
        {columns.map((c, ci) => (
          <div key={ci} className="flex-1 flex items-center gap-1">
            <Input
              value={c}
              onChange={(e) => setColumn(ci, e.target.value)}
              placeholder={`Column ${ci + 1}`}
              className={`${fieldCls} h-8 text-xs font-semibold`}
            />
            {columns.length > 1 ? (
              <button
                type="button"
                onClick={() => removeColumn(ci)}
                className="text-muted-foreground hover:text-[hsl(var(--destructive))] shrink-0"
                aria-label="Remove column"
              >
                <X size={13} />
              </button>
            ) : null}
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
          onClick={addColumn}
          aria-label="Add column"
        >
          <Plus size={14} />
        </Button>
      </div>

      {/* Body rows */}
      {rows.map((row, ri) => (
        <div key={ri} className="flex items-center gap-1.5">
          {columns.map((_, ci) => (
            <Input
              key={ci}
              value={row?.[ci] || ''}
              onChange={(e) => setCell(ri, ci, e.target.value)}
              className={`${fieldCls} h-8 text-xs flex-1`}
            />
          ))}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--destructive))] shrink-0"
            onClick={() => removeRow(ri)}
            aria-label="Remove row"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      ))}

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          className="h-7 border-border text-muted-foreground hover:text-foreground text-xs"
        >
          <Plus size={13} className="mr-1" />
          Row
        </Button>
        <Pill
          active={block.numericLast !== false}
          onClick={() => set({ numericLast: block.numericLast === false })}
        >
          Right-align last
        </Pill>
      </div>
    </div>
  );
};

const ImageBlockEditor = ({ block, set }) => {
  const [uploading, setUploading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const inputRef = useRef(null);
  const previewSrc = useResolvedImageUrl({ url: block.url, path: block.path });

  // Reset the error flag whenever a new source resolves.
  React.useEffect(() => {
    setLoadError(false);
  }, [previewSrc]);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setUploading(true);
    try {
      const prev = block.path;
      const result = await uploadPortalPhoto(file, {
        scope: 'company',
        ownerId: 'estimate',
      });
      if (!result.ok) {
        toast.error(result.error || 'Photo upload failed.');
        return;
      }
      set({ url: result.url, path: result.path });
      // Clean up the replaced object best-effort.
      if (prev && isStorageUrl(block.url)) deletePortalPhoto(block.url).catch(() => {});
    } catch (err) {
      toast.error(err?.message || 'Photo upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    if (block.url && isStorageUrl(block.url)) {
      deletePortalPhoto(block.url).catch(() => {});
    }
    set({ url: '', path: '' });
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid={`image-input-${block.id}`}
      />
      {block.url ? (
        <div className="space-y-2">
          <img
            src={previewSrc}
            alt={block.caption || 'Proposal'}
            onError={() => setLoadError(true)}
            onLoad={() => setLoadError(false)}
            className="w-full max-h-56 object-contain rounded-md border border-border bg-[hsl(var(--muted))]/30"
          />
          {loadError ? (
            <div
              className="rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 px-2.5 py-1.5 text-[11px] text-[hsl(var(--destructive))]"
              data-testid={`image-error-${block.id}`}
            >
              Couldn’t load this photo — try{' '}
              <span className="font-semibold">Replace</span> to re-upload it.
            </div>
          ) : null}
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={pick}
              disabled={uploading}
              className="h-8 border-border text-muted-foreground hover:text-foreground text-xs"
            >
              {uploading ? (
                <Loader2 size={13} className="mr-1 animate-spin" />
              ) : (
                <Upload size={13} className="mr-1" />
              )}
              Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearImage}
              className="h-8 text-muted-foreground hover:text-[hsl(var(--destructive))] text-xs"
            >
              <X size={13} className="mr-1" />
              Remove photo
            </Button>
            <div className="ml-auto flex items-center gap-1">
              <Pill
                active={block.fit !== 'cover'}
                onClick={() => set({ fit: 'contain' })}
              >
                Full
              </Pill>
              <Pill
                active={block.fit === 'cover'}
                onClick={() => set({ fit: 'cover' })}
              >
                Banner
              </Pill>
            </div>
          </div>
          <Input
            value={block.caption || ''}
            onChange={(e) => set({ caption: e.target.value })}
            placeholder="Caption (optional)…"
            className={fieldCls}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="w-full flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-[hsl(var(--muted))]/40 py-6 text-muted-foreground hover:text-foreground hover:border-[hsl(var(--primary))]/40 transition-colors"
          data-testid={`image-upload-${block.id}`}
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <ImageIcon size={20} />
          )}
          <span className="text-xs uppercase tracking-[0.16em]">
            {uploading ? 'Uploading…' : 'Upload a photo'}
          </span>
        </button>
      )}
    </div>
  );
};

const CHART_TYPES = [
  { value: 'bar', label: 'Bar' },
  { value: 'line', label: 'Line' },
  { value: 'donut', label: 'Donut' },
];

const ChartBlockEditor = ({ block, set }) => {
  const data = Array.isArray(block.data) ? block.data : [];
  const setRow = (i, patch) => {
    const next = data.map((d, idx) => (idx === i ? { ...d, ...patch } : d));
    set({ data: next });
  };
  const addRow = () => set({ data: [...data, { label: '', value: 0 }] });
  const removeRow = (i) => set({ data: data.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {CHART_TYPES.map((o) => (
          <Pill
            key={o.value}
            active={(block.chartType || 'bar') === o.value}
            onClick={() => set({ chartType: o.value })}
          >
            {o.label}
          </Pill>
        ))}
        <span className="mx-1 text-border">|</span>
        <Pill
          active={block.valueFormat !== 'money'}
          onClick={() => set({ valueFormat: 'plain' })}
        >
          Number
        </Pill>
        <Pill
          active={block.valueFormat === 'money'}
          onClick={() => set({ valueFormat: 'money' })}
        >
          Money
        </Pill>
      </div>
      <Input
        value={block.title || ''}
        onChange={(e) => set({ title: e.target.value })}
        placeholder="Chart title (optional)…"
        className={fieldCls}
      />
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input
            value={d.label || ''}
            onChange={(e) => setRow(i, { label: e.target.value })}
            placeholder="Label"
            className={`${fieldCls} h-8 text-xs flex-1`}
          />
          <Input
            type="number"
            value={d.value ?? ''}
            onChange={(e) => setRow(i, { value: Number(e.target.value) || 0 })}
            placeholder="0"
            className={`${fieldCls} h-8 text-xs w-28 tabular-nums`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--destructive))] shrink-0"
            onClick={() => removeRow(i)}
            aria-label="Remove data point"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="h-7 border-border text-muted-foreground hover:text-foreground text-xs"
      >
        <Plus size={13} className="mr-1" />
        Data point
      </Button>
    </div>
  );
};

const BlockCard = ({ block, index, count, onUpdate, onRemove, onMove }) => {
  const set = (patch) => onUpdate(block.id, patch);
  const typeLabel =
    ADD_TYPES.find((a) => a.type === block.type)?.label || block.type;

  return (
    <div
      className="rounded-md border border-border bg-[hsl(var(--card))] p-3 space-y-2"
      data-testid={`block-editor-${block.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {typeLabel}
        </span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            disabled={index === 0}
            onClick={() => onMove(block.id, -1)}
            aria-label="Move up"
            data-testid={`block-up-${block.id}`}
          >
            <ChevronUp size={14} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            disabled={index === count - 1}
            onClick={() => onMove(block.id, 1)}
            aria-label="Move down"
            data-testid={`block-down-${block.id}`}
          >
            <ChevronDown size={14} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-[hsl(var(--destructive))]"
            onClick={() => onRemove(block.id)}
            aria-label="Delete block"
            data-testid={`block-remove-${block.id}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {block.type === 'heading' ? (
        <Input
          value={block.text || ''}
          onChange={(e) => set({ text: e.target.value })}
          placeholder="Section heading…"
          className={fieldCls}
        />
      ) : null}

      {block.type === 'paragraph' ? (
        <Textarea
          value={block.text || ''}
          onChange={(e) => set({ text: e.target.value })}
          placeholder="Write a paragraph of scope…"
          className={`${fieldCls} min-h-[72px]`}
        />
      ) : null}

      {block.type === 'caveat' ? (
        <Input
          value={block.text || ''}
          onChange={(e) => set({ text: e.target.value })}
          placeholder="Disclaimer / caveat line (prints in red italic)…"
          className={fieldCls}
        />
      ) : null}

      {block.type === 'bullets' ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={block.title || ''}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="List title (optional)…"
              className={fieldCls}
            />
            <div className="flex items-center gap-1 shrink-0">
              <Pill
                active={block.variant !== 'excluded'}
                onClick={() => set({ variant: 'default' })}
              >
                Included
              </Pill>
              <Pill
                active={block.variant === 'excluded'}
                onClick={() => set({ variant: 'excluded' })}
              >
                Excluded
              </Pill>
            </div>
          </div>
          <Textarea
            value={(block.items || []).join('\n')}
            onChange={(e) => set({ items: e.target.value.split('\n') })}
            placeholder={'One bullet per line…'}
            className={`${fieldCls} min-h-[90px]`}
          />
          <p className="text-[10px] text-muted-foreground/70">
            One bullet per line.
          </p>
        </div>
      ) : null}

      {block.type === 'table' ? (
        <TableBlockEditor block={block} set={set} />
      ) : null}

      {block.type === 'image' ? (
        <ImageBlockEditor block={block} set={set} />
      ) : null}

      {block.type === 'chart' ? (
        <ChartBlockEditor block={block} set={set} />
      ) : null}

      {block.type === 'callout' ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            {TONE_OPTS.map((o) => (
              <Pill
                key={o.value}
                active={(block.tone || 'info') === o.value}
                onClick={() => set({ tone: o.value })}
              >
                {o.label}
              </Pill>
            ))}
          </div>
          <Input
            value={block.title || ''}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Callout title (optional)…"
            className={fieldCls}
          />
          <Textarea
            value={block.text || ''}
            onChange={(e) => set({ text: e.target.value })}
            placeholder="Callout body…"
            className={`${fieldCls} min-h-[64px]`}
          />
        </div>
      ) : null}
    </div>
  );
};

export const SectionBlocksEditor = ({
  blocks,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
}) => {
  const list = Array.isArray(blocks) ? blocks : [];
  return (
    <div className="space-y-3" data-testid="section-blocks-editor">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Written scope
        </span>
      </div>

      {list.length > 0 ? (
        <div className="space-y-2.5">
          {list.map((b, i) => (
            <BlockCard
              key={b.id}
              block={b}
              index={i}
              count={list.length}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onMove={onMove}
            />
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground/70">
          Add written scope — headings, paragraphs, bullet lists, callouts and
          caveats — to build a document-style proposal.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {ADD_TYPES.map(({ type, label, Icon }) => (
          <Button
            key={type}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onAdd(type)}
            className="h-8 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 text-xs"
            data-testid={`add-block-${type}`}
          >
            <Icon size={13} className="mr-1.5" />
            {label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default SectionBlocksEditor;
